import { ProCard } from '@ant-design/pro-components'
import { db } from '@app/icare/client/db'
import { localPaginatedQueryOptions } from '@app/icare/client/db/client/localQueryOptions'
import { FormStatusBadge } from '@app/icare/components/status/FormStatusBadge'
import { TherapyStatusBadge } from '@app/icare/components/status/TherapyStatusBadge'
import { queryKit } from '@app/icare/db/query/queryKit'
import { PatientOrderFormTypeEnum } from '@app/icare/db/schema/form/PatientFormType'
import { patientQueryOptions } from '@app/icare/entities/patient/queries/patientQueryOptions'
import { patientTherapyQueries } from '@app/icare/entities/patient/queries/patientTherapyQueries'
import { PatientId } from '@app/icare/entities/patient/schema/PatientId'
import type { PatientTherapyIdType } from '@app/icare/entities/patient/schema/PatientTherapy'
import {
  OrderFormStatusEnum,
  type OrderFormStatusType,
  type PatientOrderForm,
} from '@app/icare/entities/patientForm/schema/PatientForm'
import { Therapies } from '@app/icare/entities/therapy/schema/Therapy'
import type { TherapyType } from '@app/icare/entities/therapy/schema/TherapyType'
import { TherapyTypeEnum } from '@app/icare/entities/therapy/schema/TherapyType'
import { mapPatientTherapyToTherapyStatus, type TherapyStatusType } from '@app/icare/shared/api'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { entityParams } from '@pkg/db/utils'
import { formatDateTime } from '@pkg/shared/utils'
import { ActionButton, ActionColumnButton } from '@pkg/ui/button'
import { LinkIndicatorIcon } from '@pkg/ui/common'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Empty, Space, Table } from 'antd'
import type { ColumnType } from 'antd/es/table'
import { useMemo } from 'react'

export const Route = createFileRoute('/_app/patienten/$id/_edit/_documents/bedarfsermittlungen/')({
  component: RouteComponent,
  params: entityParams({ id: PatientId }),
})

interface TherapyTypeRow {
  type: TherapyType
  status: TherapyStatusType
  firstTherapyId: PatientTherapyIdType
  lastDocumentAt: string | null | undefined
  lastDocumentType: PatientOrderForm['type'] | null | undefined
  lastDocumentStatus: string | null | undefined
}

function RouteComponent() {
  const { id: patientId } = Route.useParams()
  const { data: patient } = useQuery(patientQueryOptions(patientId))
  const { data: patientTherapies = [] } = useQuery(patientTherapyQueries.forIndex(patientId))

  const therapies = patientTherapies
    .filter(therapy => {
      const therapyType = Therapies.find(t => t.type === therapy.type)
      return (therapyType?.orders.length ?? 0) > 0
    })
    .map(t => ({
      ...t,
      status: mapPatientTherapyToTherapyStatus(t, patient?.deceased),
    }))

  const { data: orderData, isFetching } = useQuery(
    localPaginatedQueryOptions({
      query: db
        .selectFrom('patientForm')
        .selectAll()
        .$call(queryKit.deleted('exclude'))
        .where('patientId', '=', patientId)
        .where('type', 'in', PatientOrderFormTypeEnum.$.zodEnum()),
      params: queryKit.parseQuery({}),
      refetchOnMount: true,
    }),
  )

  const rows: TherapyTypeRow[] = useMemo(() => {
    const forms = (orderData?.data ?? []) as unknown as PatientOrderForm[]

    const byType = new Map<TherapyType, typeof therapies>()
    for (const therapy of therapies) {
      const group = byType.get(therapy.type) ?? []
      group.push(therapy)
      byType.set(therapy.type, group)
    }

    return Array.from(byType.entries())
      .map(([type, instances]) => {
        const therapyIds = new Set(instances.map(t => t.id as PatientTherapyIdType))
        const latest = forms
          .filter(f => f.therapyId != null && therapyIds.has(f.therapyId as PatientTherapyIdType))
          .sort((a, b) => (b.createdAt?.toString() ?? '').localeCompare(a.createdAt?.toString() ?? ''))[0]

        const representative = instances[0]
        if (!representative) return undefined
        return {
          type,
          status: representative.status,
          firstTherapyId: representative.id,
          lastDocumentAt: latest?.createdAt as string | null | undefined,
          lastDocumentType: latest?.type,
          lastDocumentStatus: latest?.status,
        }
      })
      .filter((r): r is typeof r & {} => r !== undefined)
  }, [therapies, orderData])

  const columns: ColumnType<TherapyTypeRow>[] = [
    {
      key: 'type',
      title: <Trans>Therapie</Trans>,
      render: (_: unknown, record: TherapyTypeRow) => (
        <Space>
          {TherapyTypeEnum.$.getLabel(record.type)}
          <TherapyStatusBadge value={record.status} />
        </Space>
      ),
    },
    {
      key: 'lastDocumentAt',
      title: <Trans>Letztes Dokument</Trans>,
      render: (_: unknown, record: TherapyTypeRow) => formatDateTime(record.lastDocumentAt) ?? '—',
    },
    {
      key: 'lastDocumentStatus',
      title: <Trans>Status</Trans>,
      render: (_: unknown, record: TherapyTypeRow) =>
        record.lastDocumentStatus ? (
          <FormStatusBadge value={record.lastDocumentStatus as OrderFormStatusType} statusEnum={OrderFormStatusEnum} />
        ) : (
          '—'
        ),
    },
    {
      key: 'actions',
      title: <Trans>Aktionen</Trans>,
      render: (_: unknown, record: TherapyTypeRow) => (
        <ActionColumnButton
          to="/patienten/$id/bedarfsermittlungen/$therapy"
          params={{ id: patientId, therapy: record.firstTherapyId }}
          type="showEntity"
        />
      ),
    },
  ]

  if (rows.length === 0) {
    return (
      <ProCard>
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t`Es wurden bisher keine Therapien hinzugefügt.`}>
          <ActionButton
            href={`/patienten/${patientId}/therapien`}
            icon={<LinkIndicatorIcon />}
            color="primary"
            variant="filled"
            iconPlacement="end"
          >
            <Trans>Zu den Therapien wechseln</Trans>
          </ActionButton>
        </Empty>
      </ProCard>
    )
  }

  return (
    <Table<TherapyTypeRow>
      dataSource={rows}
      rowKey="type"
      columns={columns}
      size="small"
      pagination={false}
      loading={isFetching}
    />
  )
}
